import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2 } from 'lucide-react';

function MicrosoftIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

function SSOLoginForm() {
  const { loginSSO, error } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSSO = async () => {
    setLoading(true);
    try {
      await loginSSO();
    } catch (err) {
      // Error handled in context
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button
        onClick={handleSSO}
        className="w-full h-12 text-base font-medium"
        variant="outline"
        disabled={loading}
        data-testid="sso-login-button"
      >
        <MicrosoftIcon className="h-5 w-5 mr-3" />
        {loading ? 'Signing in...' : 'Sign in with Microsoft'}
      </Button>
      {error && (
        <p className="text-sm text-destructive text-center" data-testid="login-error-text">{error}</p>
      )}
      <p className="text-xs text-center text-muted-foreground mt-4">
        Uses your organisation's Microsoft 365 account
      </p>
    </div>
  );
}

export default function LoginPage() {
  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[hsl(210,20%,98%)] via-[hsl(200,35%,96%)] to-[hsl(210,20%,98%)]">
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center">
              <Building2 className="h-8 w-8 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">DeskFlow</h1>
              <p className="text-sm text-muted-foreground">Hot Desk Booking</p>
            </div>
          </div>
          <p className="text-center text-muted-foreground max-w-md text-lg leading-relaxed">
            View office floor maps, book available hot desks, see where colleagues are sitting,
            and manage desk assignments — all in one place.
          </p>
          <div className="mt-10 rounded-2xl shadow-2xl w-full max-w-lg h-64 bg-gradient-to-br from-primary/10 via-primary/5 to-background border flex items-center justify-center overflow-hidden">
            <svg viewBox="0 0 400 200" className="w-full h-full p-6 text-primary/30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="20" y="40" width="80" height="50" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1" />
              <rect x="30" y="50" width="20" height="12" rx="2" fill="currentColor" fillOpacity="0.3" />
              <rect x="60" y="50" width="30" height="6" rx="1" fill="currentColor" fillOpacity="0.2" />
              <rect x="60" y="60" width="20" height="6" rx="1" fill="currentColor" fillOpacity="0.15" />
              <rect x="120" y="40" width="80" height="50" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1" />
              <circle cx="145" cy="60" r="8" fill="currentColor" fillOpacity="0.25" />
              <rect x="160" y="55" width="30" height="5" rx="1" fill="currentColor" fillOpacity="0.2" />
              <rect x="160" y="63" width="20" height="5" rx="1" fill="currentColor" fillOpacity="0.15" />
              <rect x="220" y="40" width="80" height="50" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.05" strokeDasharray="4 4" />
              <text x="260" y="68" textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity="0.4">Available</text>
              <rect x="320" y="40" width="60" height="50" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1" />
              <circle cx="350" cy="58" r="6" fill="currentColor" fillOpacity="0.3" />
              <rect x="20" y="110" width="80" height="50" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.05" strokeDasharray="4 4" />
              <text x="60" y="138" textAnchor="middle" fontSize="10" fill="currentColor" fillOpacity="0.4">Available</text>
              <rect x="120" y="110" width="80" height="50" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1" />
              <circle cx="145" cy="130" r="8" fill="currentColor" fillOpacity="0.25" />
              <rect x="160" y="125" width="30" height="5" rx="1" fill="currentColor" fillOpacity="0.2" />
              <rect x="220" y="110" width="80" height="50" rx="4" stroke="currentColor" strokeWidth="2" fill="currentColor" fillOpacity="0.1" />
              <circle cx="245" cy="130" r="8" fill="currentColor" fillOpacity="0.25" />
              <rect x="260" y="125" width="30" height="5" rx="1" fill="currentColor" fillOpacity="0.2" />
            </svg>
          </div>
        </div>
      </div>

      {/* Right side - login form */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-6">
        <Card className="w-full max-w-md shadow-lg border">
          <CardHeader className="text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center">
                <Building2 className="h-5 w-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold">DeskFlow</span>
            </div>
            <CardTitle className="text-xl md:text-2xl">Welcome to DeskFlow</CardTitle>
            <CardDescription className="text-sm">
              Sign in with your Microsoft 365 account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <SSOLoginForm />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
